import { Component, ChangeDetectionStrategy, signal, output, input, inject, computed } from '@angular/core';
import { Router } from '@angular/router';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from '../../services/auth.service';
import { v4 as uuid } from 'uuid';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { ChromeThemeService } from '../../services/chrome-theme.service';


class NavItem {
  id?: number | string = uuid();
  name!: string;
  type!: "category" | "section" | "group" | "item" | "separator";
  tooltip?: string;
  isExpanded: boolean = false;
  icon?: string;
  route?: string;
  newRoute?: string;
  newRouteTooltip?: string;
  /** Permiso para mostrar la sección (list/search/read de la ruta). */
  permission?: string;
  /** Permiso del botón + de alta; si falta, el botón queda deshabilitado. */
  writePermission?: string;
  exact?: boolean = true;
  items: NavItem[] = [];

  constructor(partial?: Partial<NavItem>) {
    Object.assign(this, partial);
  }
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, RouterLink, RouterLinkActive, MatTooltipModule, MatButtonModule],
  styles: [`:host { display: block; height: 100%; }`]
})
export class SidebarComponent {

  toggleSidebar = output<void>();
  private router: Router = inject(Router);
  private authService = inject(AuthService);
  readonly themeService = inject(ChromeThemeService);

  close = output<void>();
  activeRoute = input<string>('');

  menuItems = signal<NavItem[]>([
    {
      id: uuid(),
      name: 'Inicio',
      type: 'category',
      route: '/forecasts/sales-operations',
      isExpanded: true,
      tooltip: 'Inicio',
      icon: 'dashboard',
      items: [
        {
          id: uuid(),
          name: 'MENÚ',
          type: 'section',
          icon: undefined,
          isExpanded: true,
          items: [
            {
              id: uuid(),
              name: 'Catálogos',
              type: 'section',
              icon: 'book',
              isExpanded: true,
              items: [
                {
                  id: uuid(),
                  name: 'Clientes',
                  type: 'group',
                  route: '/customers',
                  newRoute: '/customers/new',
                  newRouteTooltip: 'Nuevo cliente',
                  permission: 'CUSTOMER_LIST',
                  writePermission: 'CUSTOMER_WRITE',
                  icon: 'group',
                  isExpanded: true,
                  items: [],
                },
                {
                  id: uuid(),
                  name: 'Productos',
                  type: 'section',
                  route: '/products',
                  newRoute: '/products/nuevo',
                  newRouteTooltip: 'Nuevo producto',
                  permission: 'PRODUCT_LIST',
                  writePermission: 'PRODUCT_WRITE',
                  icon: 'inventory_2',
                  isExpanded: true,
                  items: [],
                },
                {
                  id: uuid(),
                  name: 'Impuestos',
                  type: 'section',
                  route: '/product-taxes',
                  newRoute: '/product-taxes/nuevo',
                  newRouteTooltip: 'Nuevo impuesto',
                  permission: 'PRODUCT_TAX_LIST',
                  writePermission: 'PRODUCT_TAX_WRITE',
                  icon: 'percent',
                  isExpanded: true,
                  items: [],
                },
                {
                  id: uuid(),
                  name: 'Planes',
                  type: 'section',
                  route: '/plans',
                  newRoute: '/plans/nuevo',
                  newRouteTooltip: 'Nuevo plan',
                  permission: 'PLAN_LIST',
                  writePermission: 'PLAN_WRITE',
                  icon: 'workspaces',
                  isExpanded: true,
                  items: [],
                },
                {
                  id: uuid(),
                  name: 'Cuentas bancarias',
                  type: 'section',
                  route: '/bank-accounts',
                  newRoute: '/bank-accounts/nuevo',
                  newRouteTooltip: 'Nueva cuenta',
                  permission: 'BANK_ACCOUNT_LIST',
                  writePermission: 'BANK_ACCOUNT_WRITE',
                  icon: 'account_balance',
                  isExpanded: true,
                  items: [],
                },
              ],
            },
            {
              id: uuid(),
              name: 'Operaciones',
              type: 'section',
              icon: 'storefront',
              isExpanded: true,
              items: [
                {
                  id: uuid(),
                  name: 'Ventas',
                  type: 'section',
                  route: '/sales',
                  newRoute: '/sales/nuevo',
                  newRouteTooltip: 'Nueva venta',
                  permission: 'SALE_LIST',
                  writePermission: 'SALE_WRITE',
                  icon: 'shopping_cart',
                  isExpanded: true,
                  items: [],
                },
                {
                  id: uuid(),
                  name: 'Ligas de pago',
                  type: 'section',
                  route: '/sale-links',
                  newRoute: '/sale-links/nuevo',
                  newRouteTooltip: 'Nueva liga de pago',
                  permission: 'SALE_LINK_LIST',
                  writePermission: 'SALE_LINK_WRITE',
                  icon: 'link',
                  isExpanded: true,
                  items: [],
                },
                {
                  id: uuid(),
                  name: 'Mandatos',
                  type: 'section',
                  route: '/mit-mandates',
                  newRoute: '/mit-mandates/nuevo',
                  newRouteTooltip: 'Nuevo mandato',
                  permission: 'MIT_MANDATE_LIST',
                  writePermission: 'MIT_MANDATE_WRITE',
                  icon: 'handshake',
                  isExpanded: true,
                  items: [],
                },
                {
                  id: uuid(),
                  name: 'Suscripciones',
                  type: 'section',
                  route: '/subscriptions',
                  newRoute: '/subscriptions/nuevo',
                  newRouteTooltip: 'Nueva suscripción',
                  permission: 'SUBSCRIPTION_LIST',
                  writePermission: 'SUBSCRIPTION_WRITE',
                  icon: 'autorenew',
                  isExpanded: true,
                  items: [],
                },
                {
                  id: uuid(),
                  name: 'Cobranza',
                  type: 'group',
                  route: '/collections',
                  icon: 'event_busy',
                  permission: 'CHARGE_SEARCH',
                  isExpanded: true,
                  items: []
                },
                {
                  id: uuid(),
                  name: 'Conciliación bancaria',
                  type: 'section',
                  route: '/bank-reconciliations',
                  icon: 'account_balance_wallet',
                  permission: 'BANK_RECONCILIATION_LIST',
                  isExpanded: true,
                  items: [],
                },
              ],
            },

            {
              id: uuid(),
              name: 'Consultas/Reportes',
              type: 'section',
              icon: 'bar_chart_4_bars',
              isExpanded: true,
              items: [
                {
                  id: uuid(),
                  name: 'Panorama operativo',
                  type: 'group',
                  route: '/forecasts/sales-operations',
                  icon: 'dashboard',
                  permission: 'FORECAST_SALES_OPERATIONS_READ',
                  isExpanded: true,
                  items: []
                },
                {
                  id: uuid(),
                  name: 'Flujo de caja',
                  type: 'group',
                  route: '/forecasts/cash-flow',
                  icon: 'monetization_on',
                  permission: 'FORECAST_CASH_FLOW_READ',
                  isExpanded: true,
                  items: []
                },
                {
                  id: uuid(),
                  name: 'Ingresos de caja',
                  type: 'group',
                  route: '/forecasts/cash-flow-v2',
                  icon: 'monetization_on',
                  permission: 'FORECAST_CASH_FLOW_READ',
                  isExpanded: true,
                  items: []
                },
                {
                  id: uuid(),
                  name: 'Retención (Churn)',
                  type: 'group',
                  route: '/forecast-churn',
                  icon: 'group_off',
                  permission: 'FORECAST_CHURN_READ',
                  isExpanded: true,
                  items: []
                },
                {
                  id: uuid(),
                  name: 'Pipeline comercial',
                  type: 'group',
                  route: '/forecast-pipeline',
                  icon: 'filter_alt',
                  permission: 'FORECAST_PIPELINE_READ',
                  isExpanded: true,
                  items: []
                },
                {
                  id: uuid(),
                  name: 'separator-consultas',
                  type: 'separator',
                  isExpanded: true,
                  items: [],
                },
                {
                  id: uuid(),
                  name: 'Calendario de cobranza',
                  type: 'group',
                  route: '/forecasts/collection-calendar',
                  icon: 'calendar_month',
                  permission: 'FORECAST_COLLECTION_CALENDAR_READ',
                  isExpanded: true,
                  items: []
                },
                {
                  id: uuid(),
                  name: 'Liquidaciones',
                  type: 'section',
                  route: '/reconciliations',
                  icon: 'account_balance',
                  permission: 'RECONCILIATION_READ',
                  isExpanded: true,
                  items: [],
                },
                {
                  id: uuid(),
                  name: 'Facturas',
                  type: 'section',
                  route: '/invoices',
                  icon: 'receipt_long',
                  permission: 'INVOICE_LIST',
                  isExpanded: true,
                  items: [],
                },
                {
                  id: uuid(),
                  name: 'Pagos',
                  type: 'section',
                  route: '/payments',
                  icon: 'payments',
                  permission: 'PAYMENT_LIST',
                  isExpanded: true,
                  items: [],
                },
                {
                  id: uuid(),
                  name: 'Cargos ondemand',
                  type: 'section',
                  route: '/mit-transactions',
                  permission: 'MIT_TRANSACTION_SEARCH',
                  icon: 'receipt_long',
                  isExpanded: true,
                  items: [],
                },
                {
                  id: uuid(),
                  name: 'Comunicados',
                  type: 'group',
                  route: '/comunicados',
                  icon: 'mail',
                  permission: 'EMAIL_LIST',
                  isExpanded: true,
                  items: [],
                },
              ],
            },

            {
              id: uuid(),
              name: 'Administración',
              type: 'section',
              icon: 'settings',
              isExpanded: true,
              items: [
                {
                  id: uuid(),
                  name: 'Usuarios',
                  type: 'section',
                  route: '/users',
                  newRoute: '/users/new',
                  newRouteTooltip: 'Nuevo usuario',
                  permission: 'USER_SEARCH',
                  writePermission: 'USER_WRITE',
                  icon: 'shield_person',
                  isExpanded: true,
                  items: [],
                },
                {
                  id: uuid(),
                  name: 'Empresa',
                  type: 'group',
                  route: '/company',
                  icon: 'business',
                  permission: 'COMPANY_READ',
                  isExpanded: true,
                  items: [],
                },
                {
                  id: uuid(),
                  name: 'Configuración',
                  type: 'group',
                  route: '/configuracion',
                  icon: 'tune',
                  permission: 'COMPANY_CONFIG_WRITE',
                  isExpanded: true,
                  items: [],
                },
                {
                  id: uuid(),
                  name: 'Webhooks',
                  type: 'group',
                  route: '/webhooks',
                  icon: 'webhook',
                  permission: 'WEBHOOK_READ',
                  writePermission: 'WEBHOOK_WRITE',
                  isExpanded: true,
                  items: [],
                },
                {
                  id: uuid(),
                  name: 'Comunicación',
                  type: 'section',
                  route: '/comunicacion',
                  newRoute: '/comunicacion/nuevo',
                  newRouteTooltip: 'Nueva plantilla',
                  permission: 'COMMUNICATION_TEMPLATE_SEARCH',
                  writePermission: 'COMMUNICATION_TEMPLATE_WRITE',
                  icon: 'mail',
                  isExpanded: true,
                  items: [],
                },
                {
                  id: uuid(),
                  name: 'Auditoría',
                  type: 'group',
                  route: '/audit',
                  icon: 'history',
                  permission: 'AUDIT_READ',
                  isExpanded: true,
                  items: [],
                }
              ],
            },

          ]
        },

      ],
    }
  ]);


  isRouteActive2 = computed(() => (route: string) => {
    return this.activeRoute() === route || this.activeRoute().startsWith(route + '/');
  });

  // Computed para determinar qué item está activo basado en la ruta
  isRouteActive = computed(() => (route: string) => {
    const currentRoute = this.activeRoute();
    return currentRoute === route || currentRoute.startsWith(route + '/');
  });

  activeCategory = computed(() => {
    return this.menuItems().find((item) => item.isExpanded);
  });

  navGroups = computed((): NavItem[] => {
    this.authService.isAuthenticated();
    const category = this.activeCategory();
    if (!category?.items?.length) return [];
    const groups = category.items[0]?.items ?? [];
    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => this.canSee(item))
      }))
      .filter((group) => group.items.length > 0);
  });

  canSee(item: NavItem): boolean {
    if (item.type === 'separator') return true;
    return !item.permission || this.authService.hasRole(item.permission);
  }

  canCreate(item: NavItem): boolean {
    return !item.writePermission || this.authService.hasRole(item.writePermission);
  }

  userPicture = computed(
    () => this.authService.profileAvatarUrlSignal() || this.authService.getUserPicture() || '/assets/avatar/avatar1.png'
  );
  userName = computed(() => this.authService.getUserDisplayName() || 'User');
  userEmail = computed(() => this.authService.getIdTokenClaims()?.email ?? '');

  sectionAccent(name: string): string {
    const accents: Record<string, string> = {
      'Catálogos': 'accent-violet',
      'Operaciones': 'accent-emerald',
      'Reportes': 'accent-amber',
      'Administración': 'accent-rose',
    };
    return accents[name] ?? 'accent-blue';
  }

  isGroupActive(group: NavItem): boolean {
    return this.hasActiveRouteInTree(group);
  }

  goToProfile(): void {
    void this.router.navigate(['/profile']);
    this.close.emit();
  }

  private hasActiveRouteInTree(item: NavItem): boolean {
    const current = this.activeRoute().split('?')[0];
    if (item.route && (current === item.route || current.startsWith(item.route + '/'))) {
      return true;
    }
    return item.items.some((child) => this.hasActiveRouteInTree(child));
  }

  toggleCategory(id: number | string): void {
    this.menuItems.update((items) =>
      items.map((item) => ({ ...item, isExpanded: item.id === id }))
    );
  }

  toggleItem(id: number | string): void {
    this.menuItems.update((items) => this.toggleItemRecursive(id, items));
  }

  toggleItemRecursive(id: number | string, items: NavItem[]): NavItem[] {
    return items.map((item) => ({
      ...item,
      isExpanded: item.id === id ? !item.isExpanded : item.isExpanded,
      items: this.toggleItemRecursive(id, item.items)
    }));
  }

  expandMenuItem(id: number | string): void {
    this.menuItems.update((items) => this.updateMenuExpand(items, id));
  }

  private updateMenuExpand(items: NavItem[], id: number | string): NavItem[] {
    return items.map((item) => {
      if (item.type == 'category') {
        return {
          ...item,
          items: item.items.length > 0 ? this.updateMenuExpand(item.items, id) : item.items
        }
      } else {
        return {
          ...item,
          isExpanded: item.id === id ? !item.isExpanded : item.isExpanded,
          items: item.items.length > 0 ? this.updateMenuExpand(item.items, id) : item.items
        }
      }
    });
  }

  navigateTo(route: string): void {
    if (route) {
      this.router.navigate([route]);
      this.close.emit();
    }
  }
}
